import { useAuthHelper } from "@/components/auth/helper";
import Logo from "@/components/logo";
import PaymentDone from "@/components/payment/PaymentDone";
import Checkout from "@/components/pricing-plans/checkout";
import PaymentVerify from "@/components/pricing-plans/payment-verify";
import PricingPlans from "@/components/pricing-plans/pricing-plans";
import useStepper from "@/hooks/useStepper";
import { calculateMonthlyPrice } from "@/utilities/functions";
import SettingsRepository from "@/utilities/repositories/Settings";
import UserRepository from "@/utilities/repositories/Users";
import { SignInResponse } from "@/utilities/types/Auths";
import { IPackage } from "@/utilities/types/Package";
import { RequestError } from "@/utilities/types/requests";
import { RegisterAgencyInput } from "@/utils/validators/create-user.schema";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Stepper } from "rizzui";
import SignUp from "../sign-up/SignUpPage";
import { EXeroPaymentStatus, IXeroPayment } from "@/utilities/types/Payment";
import { IVoucher } from "@/utilities/types/Voucher";
import step from "@/utils/signup-step";
import { IPromotionDTO } from "@/utilities/types/Promotion";

export default function RegistrationStepper() {
  const [selectedPlan, setSelectedPlan] = React.useState<IPackage | null>(null);
  const { saveAuth, clearAuth } = useAuthHelper();
  const [appliedPromotion, setAppliedPromotion] = useState<
    IPromotionDTO | undefined
  >();

  const [paymentStatus, setPaymentStatus] =
    React.useState<EXeroPaymentStatus | null>(null);

  const [xeroPayment, setXeroPayment] = useState<IXeroPayment | null>(null);

  // Using useStepper instead of manual state management
  const { currentStep, goToStep } = useStepper({
    steps: step.stepRegister.length,
    initialStep: 0, // Start at pricing plans
  });

  const handlePlanSelection = (plan: IPackage) => {
    setSelectedPlan(plan);
    goToStep(2);
  };

  const handleSettingAgencySetting = async (
    data: SignInResponse,
    salesTeamInfo: RegisterAgencyInput["salesTeamInfo"]
  ) => {
    try {
      const { user } = data || {};
      const subdomain = await SettingsRepository.generateUniqueAgencySubdomain(
        user?.id || "",
        user?.email || ""
      );
      await SettingsRepository.createAgencySetting({
        domain: subdomain || "",
        siteTitle: "",
        siteDescription: "",
        salesTeamInfo,
        phoneNumber: user?.phone || "",
      });

      //note: skip set status account, auto update from BE
      // await UserRepository.updateMe({
      //   statusAccount: "inActive",
      // });
    } catch (error) {
      throw new Error("Failed to create agency setting");
    }
  };

  const handleSignUpComplete = async (
    data: SignInResponse,
    salesTeamInfo: RegisterAgencyInput["salesTeamInfo"]
  ) => {
    try {
      toast.loading("Setting up your account...", { id: "signup-process" });
      saveAuth(data); // Save the auth token
      //await handleSettingAgencySetting(data, salesTeamInfo); disable for user config for agency
      goToStep(1); // Go to the pricing plans step
      toast.success("Account setup complete", { id: "signup-process" });
    } catch (error) {
      const message =
        error instanceof RequestError ? error.message : "Something went wrong";
      toast.error(message, { id: "signup-process" });
    }
  };

  const handleBackToPlans = () => {
    goToStep(1);
  };

  const handlePaymentComplete = async (appliedPromotion?: IPromotionDTO) => {
    try {
      toast.loading("Processing payment...", { id: "payment-process" });
      setAppliedPromotion(appliedPromotion);
      goToStep(3); // Go to payment verification step
      toast.success("Payment initiated", { id: "payment-process" });
    } catch (error) {
      const message =
        error instanceof RequestError ? error.message : "Something went wrong";
      toast.error(message, { id: "payment-process" });
    }
  };

  const handlePaymentVerified = async (payment?: IXeroPayment | null) => {
    if (payment && payment.status === "SUCCEED") {
      setXeroPayment(payment);
      // await UserRepository.updateMe({
      //   pricingPlans: payment.packageId,
      //   statusAccount: "active",
      // });
    }

    setPaymentStatus(payment?.status || EXeroPaymentStatus.PENDING);

    // goToStep(4); // Go to payment done step
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <SignUp onComplete={handleSignUpComplete} />;
      case 1:
        return <PricingPlans onPlanSelect={handlePlanSelection} />;
      case 2:
        return selectedPlan ? (
          <Checkout
            planName={selectedPlan.name}
            price={selectedPlan.price}
            monthlyPrice={calculateMonthlyPrice(
              selectedPlan.price,
              selectedPlan.duration
            )}
            onBack={handleBackToPlans}
            onNext={handlePaymentComplete}
          />
        ) : null;
      case 3:
        return selectedPlan ? (
          // <ConnectXeroWrapper>
          <PaymentVerify
            typeId={selectedPlan.id}
            onPaymentComplete={handlePaymentVerified}
            canSkip
            voucher={appliedPromotion}
            nextPage={() => goToStep(currentStep + 1)}
          />
        ) : // </ConnectXeroWrapper>
        null;
      case 4:
        return (
          <PaymentDone
            status={paymentStatus || EXeroPaymentStatus.PENDING}
            payment={xeroPayment}
          />
        );
      default:
        return null;
    }
  };

  const beforeUnload = async () => {
    await clearAuth();
  };

  React.useEffect(() => {
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, []);

  return (
    <div className="container mx-auto max-w-screen-xl px-4 py-8 md:py-16">
      <div className="mb-8 flex w-full items-center justify-center">
        <Logo className="min-w-[200px]" />
      </div>
      <div className="mb-8 md:mb-12 w-full overflow-x-auto">
        <Stepper
          currentIndex={currentStep}
          className="w-full min-w-max [&_.rizzui-step-container]:!justify-center"
        >
          {step.stepRegister.map((item, idx) => (
            <Stepper.Step
              key={idx}
              variant="outline"
              active={currentStep === idx}
              completed={currentStep > idx}
              title={<span className="hidden lg:inline">{item.title}</span>}
              description={
                <span className="hidden lg:inline">{item.description}</span>
              }
              icon={item.icon}
              className="text-xs lg:text-sm"
            />
          ))}
        </Stepper>
      </div>

      <div className="mb-6 text-center lg:hidden">
        <h2 className="text-xl font-bold">
          {step.stepRegister[currentStep]?.title}
        </h2>
        <p className="text-sm text-gray-500">
          {step.stepRegister[currentStep]?.description}
        </p>
      </div>

      {renderStep()}
    </div>
  );
}
